import React, { useState, useEffect, useRef } from 'react';
import { FormData } from '../types/participation.types';
import { tiposOrganizacion, opcionesCatalogo, opcionesPrograma, ejesInteres, areas, subareasPorArea, subejesPorEje } from '../data/participationData';
import PhoneInput from './PhoneInput';
// import OrganizationCombobox from './OrganizationCombobox'; // Temporalmente desactivado
import MultiSelectTags from './MultiSelectTags';
import SearchableSelect from './SearchableSelect';

// Lista de países para el componente PhoneInput
const countries = [
  { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷' },
  { code: 'BR', name: 'Brasil', dialCode: '+55', flag: '🇧🇷' },
  { code: 'CL', name: 'Chile', dialCode: '+56', flag: '🇨🇱' },
  { code: 'CO', name: 'Colombia', dialCode: '+57', flag: '🇨🇴' },
  { code: 'MX', name: 'México', dialCode: '+52', flag: '🇲🇽' },
  { code: 'PE', name: 'Perú', dialCode: '+51', flag: '🇵🇪' },
  { code: 'UY', name: 'Uruguay', dialCode: '+598', flag: '🇺🇾' },
  { code: 'VE', name: 'Venezuela', dialCode: '+58', flag: '🇻🇪' },
  { code: 'US', name: 'Estados Unidos', dialCode: '+1', flag: '🇺🇸' },
  { code: 'ES', name: 'España', dialCode: '+34', flag: '🇪🇸' },
  { code: 'IT', name: 'Italia', dialCode: '+39', flag: '🇮🇹' },
  { code: 'FR', name: 'Francia', dialCode: '+33', flag: '🇫🇷' },
  { code: 'DE', name: 'Alemania', dialCode: '+49', flag: '🇩🇪' },
  { code: 'GB', name: 'Reino Unido', dialCode: '+44', flag: '🇬🇧' },
  { code: 'CA', name: 'Canadá', dialCode: '+1', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'JP', name: 'Japón', dialCode: '+81', flag: '🇯🇵' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'RU', name: 'Rusia', dialCode: '+7', flag: '🇷🇺' }
];

interface ParticipationFormProps {
  onSubmit: (data: FormData) => void;
  onReset?: () => void;
  showSuccessMessage?: boolean;
}

const ParticipationForm: React.FC<ParticipationFormProps> = ({ 
  onSubmit, 
  onReset, 
  showSuccessMessage
}) => {
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    codigoPais: 'AR',
    numeroTelefono: '',
    cargo: '',
    area: '',
    subarea: '',
    organizacion: '',
    sitioWeb: '',
    tipoOrganizacion: '',
    industrias: [],
    modalidadParticipacion: [],
    catalogo: [],
    programa: [],
    objetivos: [],
    ejesInteres: [],
    mensaje: '',
    autorizoWhatsApp: false,
    deseoNovedades: false
  });

  const [emailError, setEmailError] = useState<string>('');
  const [sitioWebError, setSitioWebError] = useState<string>('');
  const [visibleTooltip, setVisibleTooltip] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [catalogoTooltipVisible, setCatalogoTooltipVisible] = useState<boolean>(false);
  const [catalogoTooltipPosition, setCatalogoTooltipPosition] = useState<{ left: number; top: number } | null>(null);
  const catalogoTooltipRef = useRef<HTMLDivElement | null>(null);
  const [modalidadTooltipVisible, setModalidadTooltipVisible] = useState<number | null>(null);
  const [modalidadTooltipPosition, setModalidadTooltipPosition] = useState<{ left: number; top: number } | null>(null);
  const modalidadTooltipRef = useRef<HTMLDivElement | null>(null);

  // Datos de modalidades de participación
  const modalidadesParticipacion = [
    {
      nombre: 'Institución Colaboradora (Invitados)',
      destinatario: 'Dirigido a organizaciones del sector académico, sector público y otros actores institucionales que deseen participar en el ecosistema de Smart City Expo, compartir sus iniciativas y articular con empresas, gobiernos y organismos internacionales.',
      beneficios: [
        'Publicación de iniciativas y proyectos institucionales en el Catálogo',
        'Presencia institucional en ecosistema de Smart City',
        'Derecho a postular speakers para el Congreso',
        'Organización de eventos paralelos (previa evaluación del equipo SCESDE)',
        'Acceso a pases para rondas de negocios',
        'Beneficios para alianzas con empresas locales, cámaras y universidades'
      ]
    },
    {
      nombre: 'Gobiernos que Innovan (Invitados)',
      destinatario: 'Dirigido a gobiernos, municipios, provincias y entidades públicas que están implementando iniciativas de innovación, transformación digital y desarrollo de ciudades inteligentes.',
      beneficios: [
        'Publicación de iniciativas y proyectos gubernamentales en el Catálogo',
        'Presencia institucional en el ecosistema de Smart City',
        'Derecho a postular speakers para el Congreso',
        'Organización de eventos paralelos (previa evaluación del equipo SCESDE)',
        'Acceso a pases para rondas de negocios',
        'Oportunidades de vinculación con empresas, universidades y organismos internacionales',
        'Visibilidad de políticas públicas y programas de innovación'
      ]
    },
    {
      nombre: 'Global Partner (Categoría Máxima | Visibilidad Total)',
      destinatario: 'Modalidades pensadas para empresas y organizaciones comerciales que buscan visibilidad, exhibición, generación de vínculos y posicionamiento institucional en el marco de Smart City Expo.',
      beneficios: [
        'Publicación destacada en el catálogo',
        'Máxima presencia institucional en todo el ecosistema',
        'Speaker asegurado en sesión oficial del Congreso',
        'Organización de eventos paralelos',
        'Stand preferencial en zona estratégica',
        'Derecho a activaciones de marca',
        'Pases prioritarios para rondas de negocios',
        'Acceso a eventos exclusivos de vinculación'
      ]
    },
    {
      nombre: 'Industry Partner (Liderazgo Sectorial)',
      destinatario: 'Modalidades pensadas para empresas y organizaciones comerciales que buscan visibilidad, exhibición, generación de vínculos y posicionamiento institucional en el marco de Smart City Expo.',
      beneficios: [
        'Publicación destacada dentro de una industria',
        'Máxima presencia institucional en el ecosistema de su sector',
        'Speaker asegurado en sesión temática del Congreso',
        'Stand preferencial en el sector de su industria',
        'Activaciones de marca',
        'Organización de eventos paralelos sectoriales',
        'Acceso prioritario a rondas de negocios sectoriales',
        'Acceso a eventos de vinculación'
      ]
    },
    {
      nombre: 'Event Partner (Presencia institucional)',
      destinatario: 'Modalidades pensadas para empresas y organizaciones comerciales que buscan visibilidad, exhibición, generación de vínculos y posicionamiento institucional en el marco de Smart City Expo.',
      beneficios: [
        'Publicación Nivel 2 en catálogo',
        'Presencia destacada en footer del ecosistema web',
        'Speaker garantizado en sesión paralela',
        'Espacio en zona expo para stand',
        'Organización de eventos paralelos',
        'Activaciones de marca',
        'Pases con prioridad 2 en rondas de negocios',
        'Acceso a eventos institucionales de vinculación'
      ]
    },
    {
      nombre: 'Business Partner (Participación con foco en networking)',
      destinatario: 'Modalidades pensadas para empresas y organizaciones comerciales que buscan visibilidad, exhibición, generación de vínculos y posicionamiento institucional en el marco de Smart City Expo.',
      beneficios: [
        'Publicación estándar en catálogo',
        'Presencia institucional en footer del ecosistema',
        'Pases a rondas de negocios',
        'Posibilidad de ampliar su participación con opciones adicionales'
      ]
    }
  ];

  // Obtener subáreas dinámicas basadas en el área seleccionada
  const getSubareasDinamicas = () => {
    if (!formData.area) {
      return [];
    }
    return subareasPorArea[formData.area as keyof typeof subareasPorArea]?.subareas || [];
  };

  const subareasDinamicas = getSubareasDinamicas();

  // Efecto para manejar el tooltip en el DOM
  useEffect(() => {
    console.log(`[USE EFFECT] Ejecutándose`, { 
      visibleTooltip, 
      tooltipPosition,
      hasVisible: visibleTooltip !== null,
      hasPosition: tooltipPosition !== null,
      both: visibleTooltip !== null && tooltipPosition !== null
    });
    
    // Limpiar tooltip existente primero
    if (tooltipRef.current && tooltipRef.current.parentNode) {
      console.log(`[USE EFFECT] Limpiando tooltip existente`);
      tooltipRef.current.parentNode.removeChild(tooltipRef.current);
      tooltipRef.current = null;
    }

    if (visibleTooltip !== null && tooltipPosition !== null) {
      const ejeSeleccionado = ejesInteres[visibleTooltip];
      const subejes = subejesPorEje[ejeSeleccionado as keyof typeof subejesPorEje]?.subejes || [];
      
      console.log(`[USE EFFECT] Creando tooltip para eje ${visibleTooltip}`, {
        eje: ejeSeleccionado,
        subejes,
        position: tooltipPosition
      });

      // Crear el elemento del tooltip
      const tooltipDiv = document.createElement('div');
      tooltipDiv.className = 'tooltip-content';
      tooltipDiv.style.cssText = `
        position: fixed !important;
        left: ${tooltipPosition.left}px !important;
        top: ${tooltipPosition.top}px !important;
        z-index: 999999 !important;
        background-color: #ffffff !important;
        border: 2px solid red !important;
        border-radius: 8px !important;
        padding: 12px 16px !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        min-width: 280px !important;
        max-width: 400px !important;
        font-family: Montserrat, sans-serif !important;
        pointer-events: none !important;
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      `;

      // Crear el contenido
      const titleDiv = document.createElement('div');
      titleDiv.style.cssText = 'font-weight: 700; color: #1e293b; font-size: 13px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb;';
      titleDiv.textContent = 'Incluye:';

      const ul = document.createElement('ul');
      ul.style.cssText = 'list-style: none; padding: 0; margin: 0;';

      subejes.forEach((subeje) => {
        const li = document.createElement('li');
        li.style.cssText = 'font-size: 12px; color: #64748b; line-height: 1.6; padding: 4px 0; padding-left: 16px; position: relative;';
        
        const bullet = document.createElement('span');
        bullet.style.cssText = 'position: absolute; left: 0; color: #CE2B15; font-weight: bold;';
        bullet.textContent = '•';
        
        li.appendChild(bullet);
        li.appendChild(document.createTextNode(subeje));
        ul.appendChild(li);
      });

      tooltipDiv.appendChild(titleDiv);
      tooltipDiv.appendChild(ul);
      
      // Verificar que document.body existe
      if (!document.body) {
        console.error('[USE EFFECT] ERROR: document.body no existe');
        return;
      }
      
      document.body.appendChild(tooltipDiv);
      tooltipRef.current = tooltipDiv;

      console.log(`[USE EFFECT] Tooltip agregado al DOM`, {
        element: tooltipDiv,
        parent: tooltipDiv.parentElement,
        position: tooltipDiv.getBoundingClientRect()
      });

      // Verificar después de un momento
      setTimeout(() => {
        const tooltipInDOM = document.querySelector('.tooltip-content');
        console.log(`[USE EFFECT] Verificación después de 100ms:`, {
          found: !!tooltipInDOM,
          element: tooltipInDOM,
          rect: tooltipInDOM ? tooltipInDOM.getBoundingClientRect() : null
        });
      }, 100);

      // Cleanup
      return () => {
        console.log(`[USE EFFECT] Cleanup ejecutándose`);
        if (tooltipRef.current && tooltipRef.current.parentNode) {
          tooltipRef.current.parentNode.removeChild(tooltipRef.current);
        }
        tooltipRef.current = null;
      };
    }
  }, [visibleTooltip, tooltipPosition]);

  // Efecto para manejar el tooltip del catálogo
  useEffect(() => {
    if (catalogoTooltipVisible && catalogoTooltipPosition) {
      const tooltip = document.createElement('div');
      tooltip.className = 'custom-tooltip';
      tooltip.style.cssText = `
        position: fixed;
        left: ${catalogoTooltipPosition.left}px;
        top: ${catalogoTooltipPosition.top}px;
        background: white;
        padding: 12px;
        border-radius: 6px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        z-index: 10000;
        max-width: 400px;
        font-family: Montserrat, sans-serif;
        border: 2px solid #ef4444;
      `;
      
      const contentDiv = document.createElement('div');
      contentDiv.style.cssText = 'font-size: 12px; color: #374151; line-height: 1.6;';
      contentDiv.textContent = 'Este catálogo es el directorio nacional del ecosistema de ciudades inteligentes de Latam. Reúne a empresas, startup, gobiernos, universidades y organizaciones con impacto, que están liderando la transformación en ciudades, territorios y comunidades. Ser parte del catálogo otorga visibilidad, vinculación estratégica, acceso a rondas de negocios, oportunidades de colaboración y presencia dentro de la red de innovación de Smart City Expo Santiago del Estero.';
      
      tooltip.appendChild(contentDiv);
      document.body.appendChild(tooltip);
      catalogoTooltipRef.current = tooltip;

      return () => {
        if (tooltip && tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip);
        }
        catalogoTooltipRef.current = null;
      };
    } else {
      if (catalogoTooltipRef.current && catalogoTooltipRef.current.parentNode) {
        catalogoTooltipRef.current.parentNode.removeChild(catalogoTooltipRef.current);
        catalogoTooltipRef.current = null;
      }
    }
  }, [catalogoTooltipVisible, catalogoTooltipPosition]);

  // Efecto para manejar el tooltip de modalidades
  useEffect(() => {
    if (modalidadTooltipVisible !== null && modalidadTooltipPosition) {
      const modalidad = modalidadesParticipacion[modalidadTooltipVisible];
      if (!modalidad) return;

      const tooltipDiv = document.createElement('div');
      tooltipDiv.className = 'tooltip-content';
      tooltipDiv.style.cssText = `
        position: fixed !important;
        left: ${modalidadTooltipPosition.left}px !important;
        top: ${modalidadTooltipPosition.top}px !important;
        background: white !important;
        z-index: 10000 !important;
        border-radius: 8px !important;
        padding: 12px 16px !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        min-width: 280px !important;
        max-width: 400px !important;
        font-family: Montserrat, sans-serif !important;
        pointer-events: none !important;
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        border: 2px solid #ef4444 !important;
      `;

      // Crear el contenido
      const destinatarioDiv = document.createElement('div');
      destinatarioDiv.style.cssText = 'font-weight: 700; color: #1e293b; font-size: 13px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb;';
      destinatarioDiv.textContent = 'Dirigido a:';

      const destinatarioText = document.createElement('div');
      destinatarioText.style.cssText = 'font-size: 12px; color: #64748b; line-height: 1.6; margin-bottom: 12px;';
      destinatarioText.textContent = modalidad.destinatario;

      const beneficiosDiv = document.createElement('div');
      beneficiosDiv.style.cssText = 'font-weight: 700; color: #1e293b; font-size: 13px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb;';
      beneficiosDiv.textContent = 'Beneficios:';

      const ul = document.createElement('ul');
      ul.style.cssText = 'list-style: none; padding: 0; margin: 0;';

      modalidad.beneficios.forEach((beneficio) => {
        const li = document.createElement('li');
        li.style.cssText = 'font-size: 12px; color: #64748b; line-height: 1.6; padding: 4px 0; padding-left: 16px; position: relative;';
        
        const bullet = document.createElement('span');
        bullet.style.cssText = 'position: absolute; left: 0; color: #CE2B15; font-weight: bold;';
        bullet.textContent = '•';
        
        li.appendChild(bullet);
        li.appendChild(document.createTextNode(beneficio));
        ul.appendChild(li);
      });

      tooltipDiv.appendChild(destinatarioDiv);
      tooltipDiv.appendChild(destinatarioText);
      tooltipDiv.appendChild(beneficiosDiv);
      tooltipDiv.appendChild(ul);
      
      document.body.appendChild(tooltipDiv);
      modalidadTooltipRef.current = tooltipDiv;

      return () => {
        if (modalidadTooltipRef.current && modalidadTooltipRef.current.parentNode) {
          modalidadTooltipRef.current.parentNode.removeChild(modalidadTooltipRef.current);
        }
        modalidadTooltipRef.current = null;
      };
    } else {
      if (modalidadTooltipRef.current && modalidadTooltipRef.current.parentNode) {
        modalidadTooltipRef.current.parentNode.removeChild(modalidadTooltipRef.current);
        modalidadTooltipRef.current = null;
      }
    }
  }, [modalidadTooltipVisible, modalidadTooltipPosition]);

  // Efecto para cerrar el tooltip de modalidades al hacer clic fuera (solo en mobile/tablet)
  useEffect(() => {
    if (modalidadTooltipVisible !== null && window.innerWidth <= 1024) {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        // Si el clic no es en el tooltip ni en un icono de modalidad, cerrar el tooltip
        if (
          modalidadTooltipRef.current &&
          !modalidadTooltipRef.current.contains(target) &&
          !target.classList.contains('modalidad-info-icon') &&
          !target.closest('.modalidad-info-icon')
        ) {
          setModalidadTooltipVisible(null);
          setModalidadTooltipPosition(null);
        }
      };

      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [modalidadTooltipVisible]);

  // Efecto para cerrar el tooltip de ejes de interés al hacer clic fuera (solo en mobile/tablet)
  useEffect(() => {
    if (visibleTooltip !== null && window.innerWidth <= 1024) {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        // Si el clic no es en el tooltip ni en un icono de información, cerrar el tooltip
        if (
          tooltipRef.current &&
          !tooltipRef.current.contains(target) &&
          !target.classList.contains('modalidad-info-icon') &&
          !target.closest('.modalidad-info-icon')
        ) {
          setVisibleTooltip(null);
          setTooltipPosition(null);
        }
      };

      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [visibleTooltip]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateUrl = (url: string): boolean => {
    if (url.trim() === '') return false; // Campo obligatorio
    
    // Si ya tiene protocolo, validar URL completa
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const urlRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
      return urlRegex.test(url);
    }
    
    // Si no tiene protocolo, validar solo dominio
    const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
    return domainRegex.test(url);
  };

  // Función para capitalizar nombres (primera letra de cada palabra en mayúscula)
  const capitalizeName = (text: string): string => {
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Aplicar capitalización automática para nombre y apellido
    if (name === 'nombre' || name === 'apellido') {
      setFormData(prev => ({
        ...prev,
        [name]: capitalizeName(value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Validar email en tiempo real
    if (name === 'email') {
      if (value.trim() === '') {
        setEmailError('');
      } else if (!validateEmail(value)) {
        setEmailError('Por favor, ingresa una dirección de correo válida');
      } else {
        setEmailError('');
      }
    }

    // Validar sitio web en tiempo real
    if (name === 'sitioWeb') {
      if (value.trim() === '') {
        setSitioWebError('');
      } else if (!validateUrl(value)) {
        setSitioWebError('Por favor, ingresa una URL válida');
      } else {
        setSitioWebError('');
      }
    }
  };

  const handleSitioWebBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    
    // Si el usuario escribió algo y no tiene protocolo, agregar https://
    if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
      // Verificar si parece un dominio válido
      const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
      if (domainRegex.test(value)) {
        setFormData(prev => ({
          ...prev,
          sitioWeb: `https://${value}`
        }));
      }
    }
  };

  // Función para manejar cambios en el tipo de organización
  const handleTipoOrganizacionChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      tipoOrganizacion: value
    }));
  };

  // Función para manejar cambios en el área
  const handleAreaChange = (value: string) => {
    setFormData(prev => {
      // Limpiar subárea si el área cambió
      const areaCambio = prev.area !== value;
      
      return {
        ...prev,
        area: value,
        subarea: areaCambio ? '' : prev.subarea
      };
    });
  };

  const handleCodigoPaisChange = (codigo: string) => {
    setFormData(prev => ({
      ...prev,
      codigoPais: codigo,
      telefono: `${countries.find(c => c.code === codigo)?.dialCode || ''} ${prev.numeroTelefono}`.trim()
    }));
  };

  const handleNumeroTelefonoChange = (numero: string) => {
    const selectedCountry = countries.find(c => c.code === formData.codigoPais);
    setFormData(prev => ({
      ...prev,
      numeroTelefono: numero,
      telefono: `${selectedCountry?.dialCode || ''} ${numero}`.trim()
    }));
  };

  // Función de validación de teléfono (misma lógica que PhoneInput)
  const validatePhoneNumber = (countryCode: string, phoneNumber: string): boolean => {
    if (!phoneNumber.trim()) return false; // Required field
    
    // Remove all non-digit characters
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    
    // Comprehensive validation patterns for all countries
    const patterns: { [key: string]: RegExp } = {
      // América Latina
      'AR': /^\d{10,11}$/, // Argentina: 10-11 digits
      'BR': /^\d{10,11}$/, // Brasil: 10-11 digits
      'CL': /^\d{8,9}$/,   // Chile: 8-9 digits
      'CO': /^\d{10}$/,    // Colombia: 10 digits
      'MX': /^\d{10}$/,    // México: 10 digits
      'PE': /^\d{9}$/,     // Perú: 9 digits
      'UY': /^\d{8,9}$/,   // Uruguay: 8-9 digits
      'VE': /^\d{10,11}$/, // Venezuela: 10-11 digits
      'BO': /^\d{8}$/,     // Bolivia: 8 digits
      'EC': /^\d{9}$/,     // Ecuador: 9 digits
      'PY': /^\d{9}$/,     // Paraguay: 9 digits
      'CR': /^\d{8}$/,     // Costa Rica: 8 digits
      'PA': /^\d{8}$/,     // Panamá: 8 digits
      'GT': /^\d{8}$/,     // Guatemala: 8 digits
      'HN': /^\d{8}$/,     // Honduras: 8 digits
      'SV': /^\d{8}$/,     // El Salvador: 8 digits
      'NI': /^\d{8}$/,     // Nicaragua: 8 digits
      'CU': /^\d{8}$/,     // Cuba: 8 digits
      'DO': /^\d{10}$/,    // República Dominicana: 10 digits
      'HT': /^\d{8}$/,     // Haití: 8 digits
      'JM': /^\d{10}$/,    // Jamaica: 10 digits
      'TT': /^\d{10}$/,    // Trinidad y Tobago: 10 digits

      // América del Norte
      'US': /^\d{10}$/,    // Estados Unidos: 10 digits
      'CA': /^\d{10}$/,    // Canadá: 10 digits

      // Europa
      'ES': /^\d{9}$/,     // España: 9 digits
      'IT': /^\d{10,11}$/, // Italia: 10-11 digits
      'FR': /^\d{10}$/,    // Francia: 10 digits
      'DE': /^\d{10,11}$/, // Alemania: 10-11 digits
      'GB': /^\d{10,11}$/, // Reino Unido: 10-11 digits
      'PT': /^\d{9}$/,     // Portugal: 9 digits
      'NL': /^\d{9}$/,     // Países Bajos: 9 digits
      'BE': /^\d{9}$/,     // Bélgica: 9 digits
      'CH': /^\d{9}$/,     // Suiza: 9 digits
      'AT': /^\d{10,11}$/, // Austria: 10-11 digits
      'SE': /^\d{9}$/,     // Suecia: 9 digits
      'NO': /^\d{8}$/,     // Noruega: 8 digits
      'DK': /^\d{8}$/,     // Dinamarca: 8 digits
      'FI': /^\d{9,10}$/,  // Finlandia: 9-10 digits
      'PL': /^\d{9}$/,     // Polonia: 9 digits
      'CZ': /^\d{9}$/,     // República Checa: 9 digits
      'HU': /^\d{9}$/,     // Hungría: 9 digits
      'RO': /^\d{10}$/,    // Rumania: 10 digits
      'BG': /^\d{9}$/,     // Bulgaria: 9 digits
      'GR': /^\d{10}$/,    // Grecia: 10 digits
      'RU': /^\d{10,11}$/, // Rusia: 10-11 digits
      'UA': /^\d{9}$/,     // Ucrania: 9 digits
      'IE': /^\d{9}$/,     // Irlanda: 9 digits
      'IS': /^\d{7}$/,     // Islandia: 7 digits

      // Asia
      'CN': /^\d{11}$/,    // China: 11 digits
      'JP': /^\d{10,11}$/, // Japón: 10-11 digits
      'KR': /^\d{10,11}$/, // Corea del Sur: 10-11 digits
      'IN': /^\d{10}$/,    // India: 10 digits
      'TH': /^\d{9}$/,     // Tailandia: 9 digits
      'SG': /^\d{8}$/,     // Singapur: 8 digits
      'MY': /^\d{9,10}$/,  // Malasia: 9-10 digits
      'ID': /^\d{10,12}$/, // Indonesia: 10-12 digits
      'PH': /^\d{10}$/,    // Filipinas: 10 digits
      'VN': /^\d{9,10}$/,  // Vietnam: 9-10 digits
      'TW': /^\d{9}$/,     // Taiwán: 9 digits
      'HK': /^\d{8}$/,     // Hong Kong: 8 digits
      'BD': /^\d{10}$/,    // Bangladesh: 10 digits
      'PK': /^\d{10}$/,    // Pakistán: 10 digits
      'LK': /^\d{9}$/,     // Sri Lanka: 9 digits
      'NP': /^\d{10}$/,    // Nepal: 10 digits
      'MM': /^\d{9,10}$/,  // Myanmar: 9-10 digits
      'KH': /^\d{8,9}$/,   // Camboya: 8-9 digits
      'LA': /^\d{8,9}$/,   // Laos: 8-9 digits
      'MN': /^\d{8}$/,     // Mongolia: 8 digits

      // Oceanía
      'AU': /^\d{9,10}$/,  // Australia: 9-10 digits
      'NZ': /^\d{8,9}$/,   // Nueva Zelanda: 8-9 digits
      'FJ': /^\d{7,8}$/,   // Fiyi: 7-8 digits
      'PG': /^\d{8}$/,     // Papúa Nueva Guinea: 8 digits
      'NC': /^\d{6}$/,     // Nueva Caledonia: 6 digits

      // África
      'ZA': /^\d{9}$/,     // Sudáfrica: 9 digits
      'EG': /^\d{10}$/,    // Egipto: 10 digits
      'NG': /^\d{10,11}$/, // Nigeria: 10-11 digits
      'KE': /^\d{9,10}$/,  // Kenia: 9-10 digits
      'MA': /^\d{9}$/,     // Marruecos: 9 digits
      'TN': /^\d{8}$/,     // Túnez: 8 digits
      'DZ': /^\d{9}$/,     // Argelia: 9 digits
      'GH': /^\d{9}$/,     // Ghana: 9 digits
      'ET': /^\d{9}$/,     // Etiopía: 9 digits
      'UG': /^\d{9}$/,     // Uganda: 9 digits
      'TZ': /^\d{9}$/,     // Tanzania: 9 digits
      'ZW': /^\d{9}$/,     // Zimbabue: 9 digits
      'BW': /^\d{7,8}$/,   // Botsuana: 7-8 digits
      'NA': /^\d{9}$/,     // Namibia: 9 digits
      'ZM': /^\d{9}$/,     // Zambia: 9 digits
      'MW': /^\d{9}$/,     // Malaui: 9 digits
      'MZ': /^\d{9}$/,     // Mozambique: 9 digits
      'MG': /^\d{9}$/,     // Madagascar: 9 digits
      'MU': /^\d{7,8}$/,   // Mauricio: 7-8 digits
      'SC': /^\d{7}$/,     // Seychelles: 7 digits

      // Oriente Medio
      'SA': /^\d{9}$/,     // Arabia Saudí: 9 digits
      'AE': /^\d{9}$/,     // Emiratos Árabes Unidos: 9 digits
      'QA': /^\d{8}$/,     // Catar: 8 digits
      'KW': /^\d{8}$/,     // Kuwait: 8 digits
      'BH': /^\d{8}$/,     // Baréin: 8 digits
      'OM': /^\d{8}$/,     // Omán: 8 digits
      'JO': /^\d{9}$/,     // Jordania: 9 digits
      'LB': /^\d{8}$/,     // Líbano: 8 digits
      'SY': /^\d{9}$/,     // Siria: 9 digits
      'IQ': /^\d{10}$/,    // Irak: 10 digits
      'IR': /^\d{10}$/,    // Irán: 10 digits
      'IL': /^\d{9}$/,     // Israel: 9 digits
      'TR': /^\d{10}$/     // Turquía: 10 digits
    };

    const pattern = patterns[countryCode];
    return pattern ? pattern.test(cleanNumber) : cleanNumber.length >= 7 && cleanNumber.length <= 15;
  };

  const isFormValid = () => {
    return formData.nombre.trim() !== '' &&
           formData.apellido.trim() !== '' &&
           formData.email.trim() !== '' &&
           validateEmail(formData.email) &&
           formData.numeroTelefono.trim() !== '' &&
           validatePhoneNumber(formData.codigoPais, formData.numeroTelefono) &&
           formData.cargo !== '' &&
           formData.area !== '' &&
           (subareasDinamicas.length === 0 || formData.subarea !== '') &&
           formData.organizacion.trim() !== '' &&
           formData.sitioWeb.trim() !== '' &&
           validateUrl(formData.sitioWeb) &&
           formData.tipoOrganizacion !== '' &&
           formData.industrias.length > 0 &&
           formData.modalidadParticipacion.length > 0 &&
           (formData.catalogo.length > 0 || formData.programa.length > 0) &&
           formData.ejesInteres.length > 0;
  };

  const getButtonText = () => {
    if (isFormValid()) {
      return 'ENVIAR CONSULTA';
    }
    return 'Completa los campos obligatorios';
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      codigoPais: 'AR',
      numeroTelefono: '',
      cargo: '',
      area: '',
      subarea: '',
      organizacion: '',
      sitioWeb: '',
      tipoOrganizacion: '',
      industrias: [],
      modalidadParticipacion: [],
      catalogo: [],
      programa: [],
      objetivos: [],
      ejesInteres: [],
      mensaje: '',
      autorizoWhatsApp: false,
      deseoNovedades: false
    });
    setEmailError('');
    setSitioWebError('');
  };


  // Función para manejar el envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid()) return;

    // Enviar formulario directamente
    submitForm();
  };

  // Función para enviar el formulario
  const submitForm = () => {
    // Crear una copia de formData con la organización en mayúsculas para el envío
    const formDataToSubmit = {
      ...formData,
      organizacion: formData.organizacion.toUpperCase()
    };
    
    onSubmit(formDataToSubmit);
    resetForm();
    if (onReset) {
      onReset();
    }
  };


  return (
    <div className={`form-container${showSuccessMessage ? ' form-container--with-success' : ''}`}>
      <h2 className="form-title">Consulta de Participación</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="nombre" className="form-label">Nombre</label>
            <input
              type="text"
              id="nombre"
              name="nombre"
              className="form-input"
              value={formData.nombre}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="apellido" className="form-label">Apellido</label>
            <input
              type="text"
              id="apellido"
              name="apellido"
              className="form-input"
              value={formData.apellido}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email" className="form-label">Correo Electrónico</label>
            <input
              type="email"
              id="email"
              name="email"
              className={`form-input ${emailError ? 'error' : ''}`}
              value={formData.email}
              onChange={handleInputChange}
              required
            />
            {emailError && (
              <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{emailError}</div>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="telefono" className="form-label">Teléfono</label>
            <PhoneInput
              codigoPais={formData.codigoPais}
              numeroTelefono={formData.numeroTelefono}
              onCodigoPaisChange={handleCodigoPaisChange}
              onNumeroTelefonoChange={handleNumeroTelefonoChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="organizacion" className="form-label">Organización</label>
            <input
              type="text"
              id="organizacion"
              name="organizacion"
              className="form-input"
              value={formData.organizacion}
              onChange={handleInputChange}
              required
              placeholder="Ingrese el nombre de la organización"
            />
            {/* OrganizationCombobox temporalmente desactivado - se puede reactivar más adelante */}
            {/* <OrganizationCombobox
              value={formData.organizacion}
              onChange={(value) => setFormData(prev => ({ ...prev, organizacion: value }))}
            /> */}
          </div>
          <div className="form-group">
            <label htmlFor="tipoOrganizacion" className="form-label">Tipo de Organización</label>
            <SearchableSelect
              value={formData.tipoOrganizacion}
              onChange={handleTipoOrganizacionChange}
              options={tiposOrganizacion}
              placeholder="Seleccionar tipo de organización..."
              allowCustomInput={false}
            />
          </div>
        </div>

        <div className="form-row">
            <div className="form-group">
              <label htmlFor="cargo" className="form-label">Cargo</label>
              <input
                type="text"
                id="cargo"
                name="cargo"
                className="form-input"
                value={formData.cargo}
                onChange={handleInputChange}
                required
                placeholder="Ingrese su cargo"
              />
            </div>
          <div className="form-group">
            <label htmlFor="area" className="form-label">Área</label>
            <SearchableSelect
              value={formData.area}
              onChange={handleAreaChange}
              options={areas}
              placeholder="Seleccionar área..."
              allowCustomInput={false}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="subarea" className="form-label">Subárea</label>
            <SearchableSelect
              value={formData.subarea}
              onChange={(value) => setFormData(prev => ({ ...prev, subarea: value }))}
              options={subareasDinamicas}
              placeholder={formData.area ? "Seleccionar subárea..." : "Primero seleccione el área"}
              allowCustomInput={false}
              disabled={!formData.area || subareasDinamicas.length === 0}
            />
          </div>
          <div className="form-group">
            <label htmlFor="sitioWeb" className="form-label">Sitio Web</label>
            <input
              type="url"
              id="sitioWeb"
              name="sitioWeb"
              className={`form-input ${sitioWebError ? 'error' : ''}`}
              value={formData.sitioWeb}
              onChange={handleInputChange}
              onBlur={handleSitioWebBlur}
              placeholder="ejemplo.com"
              required
            />
            {sitioWebError && (
              <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{sitioWebError}</div>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="industrias" className="form-label">Industrias</label>
            <MultiSelectTags
              selectedValues={formData.industrias}
              onChange={(values) => setFormData(prev => ({ ...prev, industrias: values }))}
            />
          </div>
          <div className="form-group">
            <label className="form-label form-label-optional">Modalidad de participación<span style={{ color: '#ef4444', fontWeight: 'bold', marginLeft: '2px' }}>*</span></label>
            <div className="checkbox-group">
              {modalidadesParticipacion.map((modalidad, index) => (
                <label 
                  key={index} 
                  className="checkbox-item has-tooltip"
                  onMouseEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Desactivar tooltips en tablet y mobile (<= 1024px)
                    if (window.innerWidth > 1024) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const tooltipWidth = 400;
                      const spacing = 10;
                      
                      // Calcular posición: siempre abajo, centrado horizontalmente con el elemento
                      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                      let top = rect.bottom + spacing;
                      
                      // Verificar si se sale por la derecha
                      if (left + tooltipWidth > window.innerWidth) {
                        left = window.innerWidth - tooltipWidth - 10;
                      }
                      
                      // Verificar si se sale por la izquierda
                      if (left < 10) {
                        left = 10;
                      }
                      
                      // Verificar si se sale por abajo (si no cabe abajo, mostrar arriba)
                      const tooltipHeight = 300;
                      if (top + tooltipHeight > window.innerHeight) {
                        top = rect.top - tooltipHeight - spacing;
                      }
                      
                      // Verificar si se sale por arriba después del ajuste
                      if (top < 10) {
                        top = 10;
                      }
                      
                      setModalidadTooltipPosition({ left, top });
                      setModalidadTooltipVisible(index);
                    }
                  }}
                  onMouseLeave={() => {
                    setModalidadTooltipVisible(null);
                    setModalidadTooltipPosition(null);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.modalidadParticipacion.includes(modalidad.nombre)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData(prev => ({
                          ...prev,
                          modalidadParticipacion: [...prev.modalidadParticipacion, modalidad.nombre]
                        }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          modalidadParticipacion: prev.modalidadParticipacion.filter(mod => mod !== modalidad.nombre)
                        }));
                      }
                    }}
                  />
                  <span className="checkbox-text">{modalidad.nombre}</span>
                  <span
                    className="modalidad-info-icon"
                    style={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      border: '1px solid #d1d5db',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                      marginLeft: '8px'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      
                      // Si el tooltip ya está visible para esta modalidad, cerrarlo
                      if (modalidadTooltipVisible === index) {
                        setModalidadTooltipVisible(null);
                        setModalidadTooltipPosition(null);
                        return;
                      }
                      
                      const rect = e.currentTarget.getBoundingClientRect();
                      const tooltipWidth = 400;
                      const spacing = 10;
                      
                      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                      let top = rect.bottom + spacing;
                      
                      if (left + tooltipWidth > window.innerWidth) {
                        left = window.innerWidth - tooltipWidth - 10;
                      }
                      
                      if (left < 10) {
                        left = 10;
                      }
                      
                      const tooltipHeight = 300;
                      if (top + tooltipHeight > window.innerHeight) {
                        top = rect.top - tooltipHeight - spacing;
                      }
                      
                      if (top < 10) {
                        top = 10;
                      }
                      
                      setModalidadTooltipPosition({ left, top });
                      setModalidadTooltipVisible(index);
                    }}
                  >
                    ?
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label form-label-optional" style={{ marginBottom: '30px', display: 'block' }}>
              ¿Estás interesado en presentar iniciativas, desafíos y soluciones en Smart City Expo Santiago del Estero 2027? Seleccioná las opciones de interés:<span style={{ color: '#ef4444', fontWeight: 'bold', marginLeft: '2px' }}>*</span>
            </label>
            
            <div style={{ marginBottom: '20px' }}>
              <h3 
                style={{ 
                  fontFamily: 'Montserrat, sans-serif', 
                  fontSize: '13px', 
                  fontWeight: 600, 
                  color: '#374151', 
                  marginTop: '20px',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  // Tooltip del catálogo activo en todos los tamaños de pantalla
                  const rect = e.currentTarget.getBoundingClientRect();
                  const tooltipWidth = 400;
                  const spacing = 10;
                  
                  // Calcular posición: siempre abajo, centrado horizontalmente
                  let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                  let top = rect.bottom + spacing;
                  
                  // Verificar si se sale por la derecha
                  if (left + tooltipWidth > window.innerWidth) {
                    left = window.innerWidth - tooltipWidth - 10;
                  }
                  
                  // Verificar si se sale por la izquierda
                  if (left < 10) {
                    left = 10;
                  }
                  
                  // Verificar si se sale por abajo (si no cabe abajo, mostrar arriba)
                  const tooltipHeight = 150;
                  if (top + tooltipHeight > window.innerHeight) {
                    top = rect.top - tooltipHeight - spacing;
                  }
                  
                  // Verificar si se sale por arriba después del ajuste
                  if (top < 10) {
                    top = 10;
                  }
                  
                  setCatalogoTooltipPosition({ left, top });
                  setCatalogoTooltipVisible(true);
                }}
                onMouseLeave={() => {
                  setCatalogoTooltipVisible(false);
                  setCatalogoTooltipPosition(null);
                }}
              >
                <span>FORMAR PARTE DEL CATÁLOGO "ORGANIZACIONES QUE TRANSFORMAN 2027"</span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'help',
                    border: '1px solid #d1d5db',
                    flexShrink: 0,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    // Tooltip del catálogo activo en todos los tamaños de pantalla
                    const rect = e.currentTarget.getBoundingClientRect();
                    const tooltipWidth = 400;
                    const spacing = 10;
                    
                    let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                    let top = rect.bottom + spacing;
                    
                    if (left + tooltipWidth > window.innerWidth) {
                      left = window.innerWidth - tooltipWidth - 10;
                    }
                    
                    if (left < 10) {
                      left = 10;
                    }
                    
                    const tooltipHeight = 150;
                    if (top + tooltipHeight > window.innerHeight) {
                      top = rect.top - tooltipHeight - spacing;
                    }
                    
                    if (top < 10) {
                      top = 10;
                    }
                    
                    setCatalogoTooltipPosition({ left, top });
                    setCatalogoTooltipVisible(true);
                  }}
                  onMouseLeave={(e) => {
                    e.stopPropagation();
                    setCatalogoTooltipVisible(false);
                    setCatalogoTooltipPosition(null);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Tooltip del catálogo activo en todos los tamaños de pantalla
                    const rect = e.currentTarget.getBoundingClientRect();
                    const tooltipWidth = 400;
                    const spacing = 10;
                    
                    let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                    let top = rect.bottom + spacing;
                    
                    if (left + tooltipWidth > window.innerWidth) {
                      left = window.innerWidth - tooltipWidth - 10;
                    }
                    
                    if (left < 10) {
                      left = 10;
                    }
                    
                    const tooltipHeight = 150;
                    if (top + tooltipHeight > window.innerHeight) {
                      top = rect.top - tooltipHeight - spacing;
                    }
                    
                    if (top < 10) {
                      top = 10;
                    }
                    
                    if (catalogoTooltipVisible) {
                      setCatalogoTooltipVisible(false);
                      setCatalogoTooltipPosition(null);
                    } else {
                      setCatalogoTooltipPosition({ left, top });
                      setCatalogoTooltipVisible(true);
                    }
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#e5e7eb';
                    e.currentTarget.style.color = '#374151';
                    e.currentTarget.style.borderColor = '#9ca3af';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                    e.currentTarget.style.color = '#6b7280';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                >
                  ?
                </span>
              </h3>
              <p style={{ 
                fontFamily: 'Montserrat, sans-serif', 
                fontSize: '12px', 
                fontWeight: 500, 
                color: '#000000', 
                marginBottom: '10px',
                fontStyle: 'italic',
                lineHeight: '1.6'
              }}>
                (Seleccionar una o más opciones)
              </p>
              <div className="checkbox-group">
                {opcionesCatalogo.map((opcion, index) => (
                  <label key={index} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.catalogo.includes(opcion)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            catalogo: [...prev.catalogo, opcion]
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            catalogo: prev.catalogo.filter(cat => cat !== opcion)
                          }));
                        }
                      }}
                    />
                    <span className="checkbox-text">{opcion}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <h3 style={{ 
                fontFamily: 'Montserrat, sans-serif', 
                fontSize: '13px', 
                fontWeight: 600, 
                color: '#374151', 
                marginBottom: '8px'
              }}>
                PARTICIPACIÓN EN EL PROGRAMA DEL EVENTO
              </h3>
              <p style={{ 
                fontFamily: 'Montserrat, sans-serif', 
                fontSize: '12px', 
                fontWeight: 500, 
                color: '#000000', 
                marginBottom: '10px',
                fontStyle: 'italic',
                lineHeight: '1.6'
              }}>
                (Seleccionar todas las opciones que apliquen)
              </p>
              <div className="checkbox-group">
                {opcionesPrograma.map((opcion, index) => (
                  <label key={index} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.programa.includes(opcion)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            programa: [...prev.programa, opcion]
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            programa: prev.programa.filter(prog => prog !== opcion)
                          }));
                        }
                      }}
                    />
                    <span className="checkbox-text">{opcion}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Ejes de interés</label>
            <div className="checkbox-group">
              {ejesInteres.map((eje, index) => {
                const subejes = subejesPorEje[eje as keyof typeof subejesPorEje]?.subejes || [];
                const hasSubejes = subejes.length > 0;
                
                return (
                  <label 
                    key={index} 
                    className={`checkbox-item ${hasSubejes ? 'has-tooltip' : ''}`}
                    onMouseEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log(`[HOVER ENTER] Eje ${index}: ${eje}`, {
                        hasSubejes,
                        subejesCount: subejes.length,
                        target: e.currentTarget
                      });
                      // Desactivar tooltips en tablet y mobile (<= 1024px)
                      if (hasSubejes && window.innerWidth > 1024) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const tooltipWidth = 300; // Ancho aproximado del tooltip
                        const spacing = 10;
                        
                        // Calcular posición: siempre abajo, centrado horizontalmente con el elemento
                        let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                        let top = rect.bottom + spacing;
                        
                        // Verificar si se sale por la derecha
                        if (left + tooltipWidth > window.innerWidth) {
                          left = window.innerWidth - tooltipWidth - 10;
                        }
                        
                        // Verificar si se sale por la izquierda
                        if (left < 10) {
                          left = 10;
                        }
                        
                        // Verificar si se sale por abajo (si no cabe abajo, mostrar arriba)
                        const tooltipHeight = 200; // Altura aproximada
                        if (top + tooltipHeight > window.innerHeight) {
                          top = rect.top - tooltipHeight - spacing;
                        }
                        
                        // Verificar si se sale por arriba después del ajuste
                        if (top < 10) {
                          top = 10;
                        }
                        
                        const position = { left, top };
                        console.log(`[SETTING TOOLTIP] Position calculada:`, position, 'Viewport:', { width: window.innerWidth, height: window.innerHeight });
                        setVisibleTooltip(index);
                        setTooltipPosition(position);
                        console.log(`[STATE UPDATED] visibleTooltip=${index}, tooltipPosition=`, position);
                      } else {
                        console.log(`[HOVER ENTER] No tiene subejes, no se mostrará tooltip`);
                      }
                    }}
                    onMouseLeave={() => {
                      console.log(`[HOVER LEAVE] Eje ${index}: ${eje}`);
                      setVisibleTooltip(null);
                      setTooltipPosition(null);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.ejesInteres.includes(eje)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            ejesInteres: [...prev.ejesInteres, eje]
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            ejesInteres: prev.ejesInteres.filter(e => e !== eje)
                          }));
                        }
                      }}
                    />
                    <span className="checkbox-text">{eje}</span>
                    {hasSubejes && (
                      <span
                        className="modalidad-info-icon"
                        style={{
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#f3f4f6',
                          color: '#6b7280',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          border: '1px solid #d1d5db',
                          flexShrink: 0,
                          transition: 'all 0.2s ease',
                          marginLeft: '8px'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          
                          // Si el tooltip ya está visible para este eje, cerrarlo
                          if (visibleTooltip === index) {
                            setVisibleTooltip(null);
                            setTooltipPosition(null);
                            return;
                          }
                          
                          const rect = e.currentTarget.getBoundingClientRect();
                          const tooltipWidth = 300;
                          const spacing = 10;
                          
                          let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                          let top = rect.bottom + spacing;
                          
                          if (left + tooltipWidth > window.innerWidth) {
                            left = window.innerWidth - tooltipWidth - 10;
                          }
                          
                          if (left < 10) {
                            left = 10;
                          }
                          
                          const tooltipHeight = 200;
                          if (top + tooltipHeight > window.innerHeight) {
                            top = rect.top - tooltipHeight - spacing;
                          }
                          
                          if (top < 10) {
                            top = 10;
                          }
                          
                          setTooltipPosition({ left, top });
                          setVisibleTooltip(index);
                        }}
                      >
                        ?
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="mensaje" className="form-label form-label-optional">Mensaje adicional (opcional)</label>
          <textarea
            id="mensaje"
            name="mensaje"
            className="form-textarea"
            value={formData.mensaje}
            onChange={handleInputChange}
            rows={3}
          />
        </div>

        {/* Checkboxes opcionales */}
        <div className="form-group">
          <div className="checkbox-group">
            <label className="checkbox-item no-hover">
              <input
                type="checkbox"
                checked={formData.autorizoWhatsApp}
                onChange={(e) => setFormData(prev => ({ ...prev, autorizoWhatsApp: e.target.checked }))}
              />
              <span className="checkbox-text">Autorizo contacto por WhatsApp para responder mi consulta y coordinar gestiones relacionadas.</span>
            </label>
            <label className="checkbox-item no-hover">
              <input
                type="checkbox"
                checked={formData.deseoNovedades}
                onChange={(e) => setFormData(prev => ({ ...prev, deseoNovedades: e.target.checked }))}
              />
              <span className="checkbox-text">Deseo recibir novedades y promociones por WhatsApp. Puedo darme de baja respondiendo BAJA.</span>
            </label>
          </div>
        </div>

        {/* Leyenda de políticas */}
        <div className="privacy-legend">
          <p>
            Al enviar acepto el tratamiento de mis datos según la{' '}
            <a href="https://smartcityexposantiagodelestero.com/politica-privacidad" target="_blank" rel="noopener noreferrer">
              Política de Privacidad
            </a>
            {' '}y revisé la{' '}
            <a href="https://smartcityexposantiagodelestero.com/politica-cookies" target="_blank" rel="noopener noreferrer">
              Política de Cookies
            </a>
            .
          </p>
        </div>

        <button 
          type="submit" 
          className={`btn-primary ${isFormValid() ? 'btn-active' : 'btn-inactive'}`}
          disabled={!isFormValid()}
        >
          {getButtonText()}
        </button>
        
        {/* Mensaje de éxito */}
        {showSuccessMessage && (
          <div className="success-message-inline">
            El formulario fue enviado con éxito, nos pondremos en contacto a la brevedad.
          </div>
        )}
      </form>

    </div>
  );
};

export default ParticipationForm;

